import { addDays, differenceInMilliseconds, startOfDay } from 'date-fns'
import { unescape } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import MarkdownParser from '@/utilities/client-components/components/AdaptiveCardRenderer/MarkdownParser/MarkdownProcessor'
import TextFormatter from '@/utilities/client-components/components/AdaptiveCardRenderer/TextFormatters'
import Icon from '@/utilities/client-components/components/Icon/Icon'
import * as emojiConverter from '@/utilities/client-components/utilities/emojiConverter'
import * as themeUtilities from '@/utilities/client-components/utilities/themeUtils'

const StyledActionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
`

const StyledDate = styled.span`
  font-size: 0.8em;
  color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'dark')};
`

const StyledTitle = styled.h2`
  flex: 1;
  color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'default')};
  ${({ theme, titleSize }: any) =>
    themeUtilities.getFontDefinition(theme, { size: titleSize || 'default', weight: 'bolder' })};
  margin: 0;
  padding: 0;

  a {
    color: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'link')};
  }
`

const StyledActionIcon = styled(Icon)`
  fill: ${({ theme, rootContainer }: any) => themeUtilities.getColor(theme, rootContainer, 'dark')};
`

const StyledChildrenBlock = styled.div`
  flex: 0 0 auto;
  display: flex;

  & > * {
    flex: 0 0 auto;
  }
`

interface ActionHeaderProps {
  children?: React.ReactNode
  className?: string
  icon?: string
  onAction?(...args: unknown[]): unknown
  showDate?: boolean
  title?: string
  titleSize?: string
}

const ActionHeader = ({ onAction, icon, showDate, title, titleSize, className, children }: ActionHeaderProps) => {
  const [now, setNow] = useState(new Date())
  const updateTodayTimeout = useRef()
  const { t } = useTranslation('client')

  // From a day 2 requirement that if a user sits and stares at the toolbar while the clock passes
  // midnight, the date should change (super edge case)
  useEffect(() => {
    const tomorrow = addDays(startOfDay(now), 1)
    // @ts-expect-error
    updateTodayTimeout.current = setTimeout(updateToday, differenceInMilliseconds(tomorrow, now))
  }, [])

  const updateToday = () => {
    // If you see this happen, you should go to bed
    const now = new Date()
    // @ts-expect-error
    updateTodayTimeout.current = setTimeout(updateToday, 86_400_000) // 1 day
    setNow(now)
  }

  return (
    <StyledActionHeader className={className}>
      {showDate && <StyledDate>{t('client:ActionHeader.today', { date: now })}</StyledDate>}
      {title && (
        // @ts-expect-error
        <StyledTitle titleSize={titleSize}>
          {MarkdownParser(emojiConverter.toEmoji(TextFormatter(unescape(title.toString()))))}
        </StyledTitle>
      )}
      {children && <StyledChildrenBlock>{children}</StyledChildrenBlock>}
      {icon && <StyledActionIcon name={icon} onClick={onAction} />}
    </StyledActionHeader>
  )
}

export default ActionHeader
